// ticket-file-name.ts
//
// Single source for the on-disk name of a ticket JSON under
// `.avatar/tickets/`. Ticket titles reach here from TWO untrusted-ish
// origins: the planner output at `autopilot init` and — since P6 wired
// proposal-applier into the loop — `proposed_changes[].content.title`
// emitted by a WORKER. A title such as "../../../../tmp/x" must never
// become a path component (code-reviewer 260909-p6-m2 re-review, High), so
// the slug is restricted to `[a-z0-9-]` and the id is re-validated even
// though `TicketSchema` already constrains it — defence in depth costs one
// regex.

import type { Ticket } from "./schemas/ticket-schema.js";

const TICKET_ID_PATTERN = /^T-\d{4,}$/;
const MAX_SLUG_LENGTH = 60;

/** Lowercase `[a-z0-9-]` slug, dashes collapsed/trimmed, capped in length. */
export function slugifyTicketTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/g, "");
  return slug.length > 0 ? slug : "ticket";
}

/**
 * `T-0007-some-title.json`. Throws on an id that is not `T-NNNN` so a
 * malformed ticket can never yield a name with separators or `..`.
 */
export function ticketFileName(ticket: Pick<Ticket, "id" | "title">): string {
  if (!TICKET_ID_PATTERN.test(ticket.id)) {
    throw new Error(`ticket id không hợp lệ cho tên file: ${JSON.stringify(ticket.id)}`);
  }
  return `${ticket.id}-${slugifyTicketTitle(ticket.title)}.json`;
}

/** Inverse of the id part: `T-0007-some-title.json` (or legacy `T-0007.json`) → `T-0007`; undefined for anything else. */
export function ticketIdOfFileName(fileName: string): string | undefined {
  return /^(T-\d{4,})(?:-.*)?\.json$/.exec(fileName)?.[1];
}
